import { Field, Formik, FormikProps } from 'formik';
import React, { useMemo, useRef } from 'react';
import * as Yup from 'yup';
import { HdFormikDropDown, HdFormikTextField } from '../../../form/formik/components';
import { StarWidget } from '../../../button/components/star-widget/StarWidget';
import { StarSize } from '../../../button/interfaces/starWidget';
import { HdIconButton } from '../../../button/components/icon-button/HdIconButton';
import HdButton from '../../../button/components/button/HdButton';
import HdFormControl from '../../../form/components/form-control/HdFormControl';
import HdIcon from '../../../media/components/icon/HdIcon';
import useAnalyticsTracker from '../../../analytics/hooks/useAnalyticsTracker';
import useDontMountAtFirst from '../../../core/hooks/useDontMountAtFirst';
import useService from '../../../injector/hooks/useService';
import { getDataIdGenerator } from '../../../core/utils/generateDataId';
import { NPSService } from '../../../core/services/nps.service';
import { NPS_LOW_RATING_REASONS, NPS_RATING_CATEGORY_METADATA } from './constants';
import { NPS_DIALOG_DATA_IDS } from './data-ids';
import { NPSLowRatingReason, NPSRatingCategory } from './models';
import styles from './NPSDialog.module.scss';
import { NPSDialogTrackingEvents } from './tracking';

interface FormState {
  reason: NPSLowRatingReason[];
  comment: string;
}

export interface NPSDialogFeedbackFormProps {
  feedbackId: string;
  rating: number;
  setRating: (r: number) => void;
  onSubmit: () => void;
  onClose: () => void;
}

export const dataIdGenerator = getDataIdGenerator('nps-dialog');

const dataIds = NPS_DIALOG_DATA_IDS;

const INITIAL_FORM_VALUES = {
  reason: [],
  comment: ''
};

export function NPSDialogFeedbackForm({
  feedbackId,
  rating,
  setRating,
  onSubmit,
  onClose
}: NPSDialogFeedbackFormProps) {
  const npsService = useService(NPSService);
  const formikRef = useRef<FormikProps<FormState>>();
  const { eventTrack } = useAnalyticsTracker();

  const ratingCategory = useMemo(() => {
    if (rating <= 6) {
      return NPSRatingCategory.NOT_LIKELY;
    }

    return rating <= 8 ? NPSRatingCategory.AVERAGE : NPSRatingCategory.LIKELY;
  }, [rating]);

  const {
    title,
    commentPlaceholder,
    commentLabel,
    commentRequired,
    showReason,
    reasonPlaceholder,
    requiredCommentHelpText,
    requiredReasonHelpText
  } = NPS_RATING_CATEGORY_METADATA[ratingCategory];

  const formValidationSchema = useMemo(() => {
    const schema = {
      comment: commentRequired ? Yup.string().required(requiredCommentHelpText) : Yup.string()
    };

    if (showReason) {
      schema['reason'] = Yup.array().required(requiredReasonHelpText);
    }

    return Yup.object(schema);
  }, [showReason, commentRequired, requiredCommentHelpText, requiredReasonHelpText]);

  const onRatingClicked = (_rating: number) => {
    setRating(_rating);
    npsService.setNpsScore(_rating);

    eventTrack({
      action: NPSDialogTrackingEvents.ratingsClick,
      properties: {
        rating: _rating
      }
    });
  };

  const submitFeedback = () => {
    const { comment, reason } = formikRef.current.values;
    const reasonString = reason?.map(r => r.value).join(',');

    npsService.updateRating(feedbackId, rating, comment, reasonString).subscribe();
    npsService.setNpsScore(0);

    eventTrack({
      action: NPSDialogTrackingEvents.submitFeedbackSuccess,
      properties: {
        rating,
        comment,
        reason: reasonString
      }
    });

    onSubmit();
  };

  useDontMountAtFirst(() => {
    formikRef.current.validateForm();
  }, [formValidationSchema]);

  return (
    <Formik
      initialValues={INITIAL_FORM_VALUES}
      validationSchema={formValidationSchema}
      onSubmit={submitFeedback}
      innerRef={formikRef}
    >
      {formikProps => (
        <form noValidate onSubmit={formikProps.handleSubmit}>
          <div className='dialog-body pb-0'>
            <div className='d-flex justify-content-between mt-1'>
              <StarWidget
                type={StarSize.LARGE}
                dataId={dataIds.starWidget}
                selectedStars={rating}
                onRatingClick={onRatingClicked}
              />

              <div className={styles.closeIcon}>
                <HdIconButton
                  verticalEdge='both'
                  horizontalEdge='both'
                  onClick={() => {
                    eventTrack({
                      action: NPSDialogTrackingEvents.closeDialogClick
                    });

                    onClose();
                  }}
                  dataId={dataIds.close}
                >
                  <HdIcon name='close' size={3} />
                </HdIconButton>
              </div>
            </div>

            <div className='text-subheading-3 mt-5' data-id={dataIds.title}>
              {title}
            </div>

            {showReason ? (
              <HdFormControl className='mt-5 mb-2'>
                <Field
                  id='poor-feedback-reason'
                  name='reason'
                  dataId={dataIds.reason}
                  placeholder={reasonPlaceholder}
                  valueAccessor='value'
                  multiple
                  required
                  selectMode
                  options={NPS_LOW_RATING_REASONS}
                  component={HdFormikDropDown}
                />
              </HdFormControl>
            ) : undefined}

            <HdFormControl className='mt-5 mb-2'>
              <Field
                name='comment'
                rows={4}
                resize='none'
                inputProps={{ maxLength: 1999 }}
                InputLabelProps={!!commentLabel ? { shrink: true } : undefined}
                label={commentLabel}
                placeholder={commentPlaceholder}
                required={commentRequired}
                multiline
                dataId={dataIds.comment}
                component={HdFormikTextField}
              />
            </HdFormControl>
          </div>

          <div className='dialog-footer'>
            <HdButton
              type='submit'
              dataId={dataIds.submitButton}
              onClick={() => {
                eventTrack({
                  action: NPSDialogTrackingEvents.submitFeedbackClick
                });
              }}
            >
              Submit
            </HdButton>
          </div>
        </form>
      )}
    </Formik>
  );
}
